import type { NextApiResponse } from 'next'
import { destroyCookie, setCookie } from 'nookies'
import { mappingCurrentTenantCookie } from '~/cookies/currentTenant'
import { mappingUserCookie } from '~/cookies/user'
import { AppType } from '~/core/constants/enum'
import { mappingTenantsCookie } from '../../cookies/tenants'
import type { ITenant, ITenantDetail, IUserInformation } from '../@types/global'
import {
  COOKIE_PATH,
  SESSION_COOKIE_CHUB_CURRENT_TENANT,
  SESSION_COOKIE_CHUB_USER,
  SESSION_COOKIE_CURRENT_TENANT,
  SESSION_COOKIE_CURRENT_TENANT_EXT,
  SESSION_COOKIE_EXT,
  SESSION_COOKIE_IP,
  SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM,
  SESSION_COOKIE_NAME,
  SESSION_COOKIE_NAME_CHUB,
  SESSION_COOKIE_ROLE_EXT,
  SESSION_COOKIE_ROLES,
  SESSION_COOKIE_SSO,
  SESSION_COOKIE_TENANTS,
  SESSION_COOKIE_USER,
  SESSION_CSRF_SECRET_COOKIE,
  SESSION_COOKIE_SIGNED_TOKEN,
  SESSION_COOKIE_CHUB_SIGNED_TOKEN,
  SESSION_ERROR_MESSAGE,
  SESSION_EXPIRES_AT_COOKIE_NAME
} from '../constants/cookies'
import { removeAttributesFromServer } from '../utilities/common'

const getValidExpiresIn = (expiresIn: number) => {
  return typeof expiresIn === 'number' && expiresIn > 0 && !isNaN(expiresIn) ? expiresIn : 86400
}

export function setSessionCookieClient(cookieName: string, cookieValue: string, expiresIn: number) {
  const options = {
    maxAge: getValidExpiresIn(expiresIn),
    path: COOKIE_PATH
  }

  setCookie(null, cookieName, cookieValue, options)
}

export function setSessionCookieExtClient(
  cookieName: string,
  cookieValue: string,
  expiresIn: number
) {
  const options = {
    maxAge: getValidExpiresIn(expiresIn),
    path: COOKIE_PATH,
    secure: true,
    sameSite: 'Lax' as const
  }

  setCookie(null, cookieName, cookieValue, options)
}

export async function saveSessionCookie(
  res: NextApiResponse,
  jsonFormat: {
    token: string
    signedToken?: string
    altAppTokens?: [{ app: string; token: string; onboarding?: boolean; signed_token?: string }]
    user?: {
      data?: {
        attributes?: {
          tenants?: {
            data: Array<{ attributes: ITenant }>
          }
          roles?: { data: Array<{ attributes: { id: string; name: string } }> }
          userTenants?: {
            data?: Array<{ attributes: { userKind: string } }>
          }
        }
      }
    }
    currentTenant?: {
      data: {
        attributes: ITenantDetail
      }
    }
    errors?: Array<{
      message: string
      extensions: {
        error_code: string
        http_code: number
        errors: Array<{
          field: string
          message: string
        }>
      }
    }>
  },
  expiresIn: number,
  extraParams: {
    ip?: string | string[]
    existingCookies?: Partial<{ [key: string]: string }>
    isCareerHub?: boolean
  }
) {
  if (jsonFormat?.errors && jsonFormat?.errors?.length > 0) {
    await setSessionCookie(
      res,
      SESSION_ERROR_MESSAGE,
      JSON.stringify({ errors: jsonFormat?.errors }).toString(),
      expiresIn
    )
  } else {
    if (extraParams.ip) {
      await setSessionCookie(res, SESSION_COOKIE_IP, String(extraParams.ip), expiresIn)
    }

    const user = {
      ...(jsonFormat?.user?.data?.attributes || {}),
      userTenants:
        jsonFormat?.user?.data?.attributes?.userTenants?.data &&
        jsonFormat?.user?.data?.attributes?.userTenants?.data.length > 0
          ? [
              {
                userKind: (jsonFormat?.user?.data?.attributes || {})?.userTenants?.data?.[0]
                  ?.attributes?.userKind
              }
            ]
          : []
    } as unknown as IUserInformation

    const rolesObj = {
      data: removeAttributesFromServer(jsonFormat?.user?.data?.attributes?.roles?.data || [])
    }
    const currentRoleFilter = rolesObj.data.filter((item) => item?.id)
    const currentRoleName = currentRoleFilter.length ? currentRoleFilter?.[0]?.name : ''

    const tenantsList = JSON.stringify(
      mappingTenantsCookie(
        removeAttributesFromServer(jsonFormat?.user?.data?.attributes?.tenants?.data || [])
      )
    ).toString()

    const currentTenant = jsonFormat?.currentTenant?.data
      ? mappingCurrentTenantCookie(jsonFormat.currentTenant.data?.attributes)
      : null

    // Helper function to save Internal cookies
    const saveInternalCookies = (token: string, onboarding?: boolean, signedToken?: string) => {
      const signedTokenValue = signedToken ?? jsonFormat?.signedToken
      setSessionCookie(res, SESSION_COOKIE_NAME, token, expiresIn)
      setSessionCookieExt(res, SESSION_COOKIE_EXT, token, expiresIn)
      if (signedTokenValue) {
        setSessionCookie(res, SESSION_COOKIE_SIGNED_TOKEN, signedTokenValue, expiresIn)
      }
      setSessionCookie(
        res,
        SESSION_COOKIE_USER,
        JSON.stringify(
          mappingUserCookie({
            ...user,
            onboarding: onboarding ?? user.onboarding
          })
        ),
        expiresIn
      )
      setSessionExpiryCookie(res, SESSION_EXPIRES_AT_COOKIE_NAME, expiresIn)
      setSessionCookieExt(
        res,
        SESSION_COOKIE_ROLE_EXT,
        JSON.stringify(currentRoleName).toString(),
        expiresIn
      )
      setSessionCookie(res, SESSION_COOKIE_TENANTS, tenantsList, expiresIn)
      if (currentTenant) {
        setSessionCookie(
          res,
          SESSION_COOKIE_CURRENT_TENANT,
          JSON.stringify(currentTenant).toString(),
          expiresIn
        )
        setSessionCookieExt(
          res,
          SESSION_COOKIE_CURRENT_TENANT_EXT,
          JSON.stringify(currentTenant).toString(),
          expiresIn
        )
      }
    }

    // Helper function to save CHUB cookies
    const saveChubCookies = (token: string, onboarding?: boolean, signedToken?: string) => {
      const signedTokenValue = signedToken ?? jsonFormat?.signedToken
      setSessionCookie(res, SESSION_COOKIE_NAME_CHUB, token, expiresIn)
      if (signedTokenValue) {
        setSessionCookie(res, SESSION_COOKIE_CHUB_SIGNED_TOKEN, signedTokenValue, expiresIn)
      }
      setSessionCookie(
        res,
        SESSION_COOKIE_CHUB_USER,
        JSON.stringify(
          mappingUserCookie({
            ...user,
            onboarding: onboarding ?? user.onboarding
          })
        ),
        expiresIn
      )
      if (currentTenant) {
        setSessionCookie(
          res,
          SESSION_COOKIE_CHUB_CURRENT_TENANT,
          JSON.stringify(currentTenant).toString(),
          expiresIn
        )
      }
    }

    const altAppTokens = jsonFormat.altAppTokens || []
    if (extraParams.isCareerHub) {
      // ===== CASE: Login from CareerHub =====
      const altUserToken = altAppTokens.find((a) => a.app === AppType.INTERNAL_APP)
      const hasExistingInternalCookies = !!(
        extraParams.existingCookies?.sessionUser || extraParams.existingCookies?.session
      )
      // 1. Save cookie for CHUB (jsonFormat.token)
      saveChubCookies(jsonFormat.token)

      // 2. If altAppTokens has 'user' and no existing internal cookies → save internal cookies
      if (altUserToken && !hasExistingInternalCookies) {
        saveInternalCookies(altUserToken.token, altUserToken.onboarding, altUserToken.signed_token)
      }
      return
    }

    // ===== CASE: Login from Internal =====
    const altChubToken = altAppTokens.find((a) => a.app === AppType.CHUB_APP)
    const hasExistingChubCookies = !!(
      extraParams.existingCookies?.sessionChubUser || extraParams.existingCookies?.sessionChub
    )
    // 1. Save cookie for Internal (jsonFormat.token)
    saveInternalCookies(jsonFormat.token)

    // 2. If altAppTokens has 'chub_user' and no existing CHUB cookies → save CHUB cookies
    if (altChubToken && !hasExistingChubCookies) {
      saveChubCookies(altChubToken.token, altChubToken.onboarding, altChubToken.signed_token)
    }
  }
}

function setSessionCookie(
  res: NextApiResponse,
  cookieName: string,
  cookieValue: string,
  expiresIn: number
) {
  const options = {
    maxAge: getValidExpiresIn(expiresIn),
    path: COOKIE_PATH
  }

  // when the session-cookie gets created
  // we store it as an httpOnly cookie (important!)
  setCookie({ res }, cookieName, cookieValue, options)
}

function setSessionCookieExt(
  res: NextApiResponse,
  cookieName: string,
  cookieValue: string,
  expiresIn: number
) {
  const options = {
    maxAge: getValidExpiresIn(expiresIn),
    path: COOKIE_PATH,
    httpOnly: false,
    secure: true,
    sameSite: 'Lax' as const
  }

  // when the session-cookie gets created
  // we store it as an httpOnly cookie (important!)
  setCookie({ res }, cookieName, cookieValue, options)
}

function setSessionExpiryCookie(res: NextApiResponse, cookieName: string, expiresIn: number) {
  const validExpiresIn = getValidExpiresIn(expiresIn)

  const options = {
    maxAge: validExpiresIn,
    path: COOKIE_PATH
  }

  // when the session-cookie gets created
  // we store it as an httpOnly cookie (important!)
  setCookie({ res }, cookieName, validExpiresIn.toString(), options)
}

export function destroySessionCookies(res: NextApiResponse) {
  const options = { path: COOKIE_PATH }

  destroyCookie({ res }, SESSION_COOKIE_NAME, options)
  destroyCookie({ res }, SESSION_COOKIE_EXT, options)
  destroyCookie({ res }, SESSION_COOKIE_USER, options)
  destroyCookie({ res }, SESSION_EXPIRES_AT_COOKIE_NAME, options)
  destroyCookie({ res }, SESSION_COOKIE_ROLES, options)
  destroyCookie({ res }, SESSION_COOKIE_ROLE_EXT, options)
  destroyCookie({ res }, SESSION_COOKIE_TENANTS, options)
  destroyCookie({ res }, SESSION_COOKIE_CURRENT_TENANT, options)
  destroyCookie({ res }, SESSION_COOKIE_CURRENT_TENANT_EXT, options)
  destroyCookie({ res }, SESSION_CSRF_SECRET_COOKIE, options)
  destroyCookie({ res }, SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM, options)
  destroyCookie({ res }, SESSION_COOKIE_SSO, options)
  destroyCookie({ res }, SESSION_COOKIE_IP, options)
  destroyCookie({ res }, SESSION_COOKIE_SIGNED_TOKEN, options)
}
